import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, useMediaQuery, Grid, Tabs, Tab } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';

import DataGridProComponent from '../../components/DataGrid/DataGridProComponent';
import InfoCard from '../../components/InfoCard/InfoCard';
import { CustomTabPanel } from '../../components/CustomTabPanel/CustomTabPanel';
import { type RootState } from '../../redux/store';
import { INVOICE_NUMBER, INVOICES, setTableMessage, SUMMARY } from '../../constants/messages.tsx';
import './Invoices.scss';
import { useLazyGetInvoicesQuery, useGetInvoicePageSummaryQuery } from '../../api/invoice.api';
import type { InvoicePageSummary } from '../../api/models/invoicePageSummary.model';
import { selectInvoiceView } from '../../redux/slices/uiSettingsSlice';
import {
	setSelectedInvoiceDetailsViewId,
	clearSelectedInvoiceDetailsViewId,
} from '../../redux/slices/invoiceSlice';
import {
	invoiceSummaryColumns,
	billingInfoColumns,
	mobileInvoiceColumns,
} from '../../components/DataGrid/columnDefinitions';
import InvoiceDetails from './InvoiceDetails/InvoiceDetails.tsx';
import { InvoicesDataGridType } from '../../components/DataGrid/DataGridTypes.ts';
import type { Invoice } from '../../api/models/invoice.model.ts';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import { selectAccount } from '../../redux/slices/accountSlice.ts';
import { FormatCurrency, FormatCurrencyCompact } from '../../utils/helpers';

interface TabType {
	key: string;
	title: string;
	closeable: boolean;
	invoice?: Invoice;
}

const Invoices: React.FC = () => {
	const account = useSelector(selectAccount)!;
	const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
	const viewType = useSelector(selectInvoiceView);
	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);
	const selectedInvoiceDetailsViewId = useSelector(
		(state: RootState) => state.invoices.selectedInvoiceDetailsViewId
	);
	const dispatch = useDispatch();
	const [getInvoices] = useLazyGetInvoicesQuery();

	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [dataIsLoading, setDataIsLoading] = useState(false);

	const {
		data: invoicePageSummary = {} as InvoicePageSummary,
		isLoading: invoicePageSummaryLoading,
		isError: invoiceIsError,
	} = useGetInvoicePageSummaryQuery(
		{
			accountId: account?.accountId,
			accountTypeCode: account?.accountTypeCode,
			sourceId: account?.sourceId,
			filters: activeFilters,
		},
		{
			skip: !account?.accountId || !account?.accountTypeCode || !account?.sourceId,
		}
	);

	// GridDataSource for server-side fetching
	const invoiceDataSource: GridDataSource = useMemo(
		   () => ({
			   getRows: async ({ paginationModel, sortModel }) => {
				   if (!account.accountId) {
					   return {
						   rows: [],
						   rowCount: 0,
					   };
				   }
				   setDataIsLoading(true);
				   const invoiceQueryParams = {
					   accountId: account.accountId,
					   sourceId: account.sourceId,
					   accountTypeCode: account.accountTypeCode,
					   filters: activeFilters,
					   pageSize: paginationModel?.pageSize ?? 10,
					   currentPage: paginationModel?.page ?? 0,
					   sortDirection: sortModel?.[0]?.sort || '',
					   sortField: sortModel?.[0]?.field || '',
				   };
				   try {
					   const result = await getInvoices(invoiceQueryParams, true).unwrap();
					   setInvoices(result?.content || []);
					   setDataIsLoading(false);
					   return {
						   rows: result?.content ?? [],
						   rowCount: result?.totalElements ?? 0,
					   };
				   } catch (error: any) {
					   setDataIsLoading(false);
					   dispatch({
						   type: 'alert/showAlert',
						   payload: {
							   message: error?.message || 'Failed to load invoices',
							   type: 'error',
						   },
					   });
					   return { rows: [], rowCount: 0 };
				   }
			   },
		   }),
		   [account, activeFilters, dispatch]
	   );



	// Listen for selectedInvoiceDetailsViewId changes (details tab only)
	React.useEffect(() => {
		if (selectedInvoiceDetailsViewId) {
			const foundInvoice = invoices.find((i) => i.invoiceNumber === selectedInvoiceDetailsViewId);
			if (foundInvoice) {
				handleInvoiceClick(foundInvoice);
			}
		}
	}, [selectedInvoiceDetailsViewId]);

	// Tabs state
	const [tabs, setTabs] = useState<TabType[]>([
		{ key: 'summary', title: SUMMARY, closeable: false },
	]);
	const [activeTabKey, setActiveTabKey] = useState('summary');

	// Handle click on invoice number to open details tab
	const handleInvoiceClick = useCallback(
		(invoice: Invoice) => {
			const key = `invoice-${invoice.invoiceNumber}`;
			if (!tabs.find((tab) => tab.key === key)) {
				setTabs((prev) => [
					...prev,
					{
						key,
						title: `Invoice ${invoice.invoiceNumber}`,
						closeable: true,
						invoice,
					},
				]);
			}
			setActiveTabKey(key);
			dispatch(setSelectedInvoiceDetailsViewId(invoice.invoiceNumber));
		},
		[tabs]
	);

	// Get columns based on the current view and device
	const getColumnsForView = () => {
		let columns;
		if (isMobile) {
			columns = mobileInvoiceColumns;
		} else {
			columns = viewType === 'billingInfo' ? billingInfoColumns : invoiceSummaryColumns;
		}
		return columns;
	};

	// Render main invoices table tab
	const renderInvoiceSummaryTabView = () => (
		<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
			{setTableMessage(INVOICE_NUMBER)}
			<DataGridProComponent
				columns={getColumnsForView()}
				dataGridType={InvoicesDataGridType}
				dataSource={invoiceDataSource}
				dataIsLoading={dataIsLoading}
			/>
		</Box>
	);

	// Render tab content
	const renderTabView = (tab: TabType) => {
		if (tab.key === 'summary') {
			return renderInvoiceSummaryTabView();
		}
		if (tab.key.startsWith('invoice-')) {
			return (
				<Box sx={{ width: '100%', mt: 3, padding: 0 }}>
					<InvoiceDetails />
				</Box>
			);
		}
		return null;
	};

	// Handle tab close
	const handleCloseTab = (key: string) => {
		setTabs((prev) => prev.filter((tab) => tab.key !== key));
		if (activeTabKey === key) {
			setActiveTabKey('summary');
		}
		dispatch(clearSelectedInvoiceDetailsViewId());
	};

	const formatInvoiceSummaryValues = (
		key: keyof InvoicePageSummary,
		formatCurrency: boolean = false
	): string | number => {
		if (invoiceIsError) return 'Error';
		if (invoicePageSummaryLoading) return 'Loading...';
		const value = invoicePageSummary[key] ?? 'N/A';

		if (!formatCurrency || !(typeof value === 'number')) {
			return value;
		}

		// If we have currency at or above $10,000,000 (8 digits or more) show in compact form of $10M to avoid text overflowing card
		const absValue = Math.abs(value);
		if (absValue >= 10_000_000) {
			return FormatCurrencyCompact(value);
		}

		return FormatCurrency(value, {rounding: true});
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
				<Typography
					variant="h4"
					component="h1"
					sx={{ 
						color: (theme) => theme.palette.brand.primary, 
						fontWeight: 'bold',
						ml: '0.75rem',
					}}
				>
					{INVOICES}
				</Typography>
			</Box>

			<Grid
				container
				spacing={2}
				sx={{
					mb: 3,
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					justifyContent: isMobile ? 'space-evenly' : 'flex-start',
					alignItems: 'stretch',
					margin: isMobile ? '0 auto' : undefined,
				}}
			>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={formatInvoiceSummaryValues('totalInvoices', false)}
						subheading="Invoices"
						sx={{ flexGrow: 1 }}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={formatInvoiceSummaryValues('totalInvoiceBalance', true)}
						subheading="Total Invoice Balance"
						sx={{ flexGrow: 1 }}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={formatInvoiceSummaryValues('totalPastDueInvoices')}
						subheading="Total Past Due Invoices"
						sx={{ flexGrow: 1 }}
					/>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }}>
					<InfoCard
						heading={formatInvoiceSummaryValues('totalAmountPastDue', true)}
						subheading="Total Amount Past Due"
						sx={{ flexGrow: 1 }}
					/>
				</Grid>
			</Grid>

			<Box sx={{ width: '100%', mb: 3, display: 'flex', flexDirection: 'column' }}>
				<Tabs
					value={activeTabKey}
					onChange={(_, v) => setActiveTabKey(v)}
					aria-label="invoices-tabs"
					variant="scrollable"
					scrollButtons="auto"
					sx={{ float: 'left', borderBottom: '1px solid' }}
				>
					{tabs.map((tab) => (
						<Tab
							key={tab.key}
							label={
								<span className="invoice-tab">
									{tab.title}
									{tab.closeable && (
										<CloseIcon
											fontSize="small"
											sx={{ ml: 1, cursor: 'pointer' }}
											onClick={(e) => {
												e.stopPropagation();
												handleCloseTab(tab.key);
											}}
										/>
									)}
								</span>
							}
							value={tab.key}
							id={`tab-${tab.key}`}
							aria-controls={`tabpanel-${tab.key}`}
							data-testid={`tab-${tab.key}`}
						/>
					))}
				</Tabs>
				{tabs.map((tab) => (
					<CustomTabPanel
						key={tab.key}
						selected={activeTabKey}
						value={tab.key}
						className="tab-container"
					>
						{renderTabView(tab)}
					</CustomTabPanel>
				))}
			</Box>
		</Box>
	);
};

export default Invoices;
